/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Grid, HStack, Text, VStack } from '@chakra-ui/react';
import { useBookActivities, useBooks, useFeaturedBooks, useMothlyBestSellers } from '@partiu/api/bookstore/hooks';
import { cloneElement, useEffect } from 'react';
import { WhatsappFloatingBall } from '../../../../packages/ui/components/atoms/WhatsappFloatingBall';
import { BannerCarousel } from '../../../../packages/ui/components/molecules/Carousel';
import { Labeled } from '../../../../packages/ui/components/molecules/Labeled';
import { useTheme } from '../../../../packages/ui/contexts/ThemeProvider';
import { BookActivityCard } from '../components/atoms/BookActivityCard';
import { MainTemplate } from '../templates/MainTemplate';
import { useLocation, useNavigate } from 'react-router-dom';
import { GoStar } from 'react-icons/go';
import { RiHome2Line } from 'react-icons/ri';
import useIsMobile from '../useIsMobile';
import { HorizontalBooks } from '../components/molecules/HorizontalBooks';
import { CurrentBook } from '../components/molecules/CurrentBook';
import { useProfile } from '../../../../packages/ui/contexts/ProfileProvider';

interface Tab {
  name: string;
  icon: JSX.Element;
  path: string;
}

export const RenderNativeTabs: React.FC = () => {
  const { colors, isNative, bookstoreTypes } = useTheme();
  const tabs: Tab[] = [
    { name: 'Home', icon: <RiHome2Line />, path: '/' },
    {
      name: bookstoreTypes.includes('magazine') ? 'Banca' : 'Acervo',
      icon: <GoStar />,
      path: bookstoreTypes.includes('magazine') ? '/banca' : '/acervo',
    },
    {
      name: bookstoreTypes.includes('magazine') ? 'Minhas Revistas' : 'Minha Biblioteca',
      icon: <GoStar />,
      path: '/biblioteca',
    },
  ];
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <>
      {isNative && (
        <HStack
          position={'fixed'}
          width={'100%'}
          alignItems={'center'}
          justifyContent={'center'}
          top={0}
          zIndex={999}
          backgroundColor="#f1f4f5"
        >
          {tabs.map((tab, i) => (
            <Box
              key={i}
              marginRight={'0px'}
              cursor={'pointer'}
              flexGrow={1}
              padding="10px 0px"
              onClick={() => navigate(`${tab.path}${isNative ? '?isNative=true' : ''}`)}
              color={tab.path === location.pathname ? colors.primaryColor : 'black'}
              paddingBottom={'5px'}
            >
              <VStack spacing={1}>
                {cloneElement(tab.icon, { fontSize: '1.2em' })}
                <Text fontSize={'0.9em'} whiteSpace={'nowrap'}>
                  {tab.name}
                </Text>
              </VStack>
            </Box>
          ))}
        </HStack>
      )}
      {isNative && <Box marginBottom="50px" />}
    </>
  );
};

const BannerAndCurrentBook: React.FC = () => {
  const { banners } = useTheme();

  return (
    <VStack w={'100%'} align={'flex-start'}>
      <BannerCarousel banners={banners.filter((b) => b.type === 'book')} />
      <CurrentBook />
    </VStack>
  );
};

interface BookActivitiesSectionProps {
  bookActivities: any;
}

const BookActivitiesSection: React.FC<BookActivitiesSectionProps> = ({ bookActivities }) => {
  const isMobile = useIsMobile();
  return (
    (bookActivities?.results?.length ?? 0) > 0 && (
      <VStack w={'100%'} align={'flex-start'}>
        {isMobile ? (
          <Labeled
            paddingX={'5%'}
            w={'100%'}
            labelColor="rgba(0,0,0,0.6)"
            labelSize={'20px'}
            spacing={4}
            label={'Explore por categoria'}
            helpText="Aqui você encontra categorias de livros para explorar. Clique em uma categoria para ver os livros disponíveis."
          >
            <HStack
              spacing={4}
              maxWidth={'100%'}
              overflowX={'auto'}
              css={{
                '&::-webkit-scrollbar': { display: 'none' },
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none',
              }}
            >
              {bookActivities?.results.map((ba: any, i: number) => <BookActivityCard key={i} activity={ba} />)}
            </HStack>
          </Labeled>
        ) : (
          <Box paddingX={'5%'} width={'100%'}>
            <Labeled
              w={'100%'}
              labelColor="rgba(0,0,0,0.6)"
              labelSize={'20px'}
              spacing={4}
              paddingRightActive="5%"
              label={'Explore por categoria'}
              helpText="Aqui você encontra categorias de livros para explorar. Clique em uma categoria para ver os livros disponíveis."
            >
              <Grid width={'100%'} templateColumns="repeat(5, 1fr)" gap={2}>
                {bookActivities?.results.map((ba: any, i: number) => <BookActivityCard key={i} activity={ba} />)}
              </Grid>
            </Labeled>
          </Box>
        )}
      </VStack>
    )
  );
};

interface BooksOfTheMonthSectionProps {
  recurringBook: boolean;
  audiobook?: boolean;
}

export const BooksOfTheMonthSection: React.FC<BooksOfTheMonthSectionProps> = ({ recurringBook, audiobook }) => {
  const { bookstoreTypes, name, slug } = useTheme();

  console.log('slug', slug);
  return (
    <VStack align={'flex-start'} w={'100%'} spacing={5}>
      {bookstoreTypes.includes('book') && recurringBook && !audiobook && (
        <HorizontalBooks
          name={'Livros no mês'}
          receivedBook
          params={{ type: 'book' }}
          hookApi={useFeaturedBooks}
          description={
            recurringBook
              ? ['abrintbiblioteca', 'abrintrevistas'].includes(slug)
                ? 'Confira nossos livros do mês e aproveite!'
                : 'Abaixo estão os livros do mês, adquiridos como parte do seu combo e são seus para sempre! Você irá receber até o dia 05.'
              : 'Abaixo estão os livros do mês. Se você realizou uma recarga este mês, esses livros são seus para sempre. Acesse o menu de recebidos para conferir.'
          }
        />
      )}
      {bookstoreTypes.includes('audiobook') && (
        <HorizontalBooks
          name={'Audiolivros do mês'}
          params={{ type: 'audiobook' }}
          receivedBook
          hookApi={useFeaturedBooks}
          description={
            recurringBook
              ? ['abrintbiblioteca', 'abrintrevistas'].includes(slug)
                ? 'Confira nossos audiolivros do mês e aproveite!'
                : 'Abaixo estão os audiolivros do mês, adquiridos como parte do seu combo e são seus para sempre! Você irá receber até o dia 05.'
              : 'Abaixo estão os audiolivros do mês. Se você realizou uma recarga este mês, esses audiolivros são seus para sempre. Acesse o menu de recebidos para conferir.'
          }
        />
      )}
      {bookstoreTypes.includes('magazine') && (
        <HorizontalBooks
          name={'Revistas do mês'}
          params={{ type: 'magazine' }}
          receivedBook
          hookApi={useFeaturedBooks}
          description={
            recurringBook
              ? ['abrintbiblioteca', 'abrintrevistas'].includes(slug)
                ? 'Confira nossas revistas do mês e aproveite!'
                : 'Abaixo estão as revistas do mês, adquiridas como parte do seu combo e são suas para sempre! Você irá receber até o dia 15.'
              : 'Abaixo estão as revistas do mês. Se você realizou uma recarga este mês, essas revistas são suas para sempre. Acesse o menu de recebidos para conferir.'
          }
        />
      )}
    </VStack>
  );
};

export const HomePage: React.FC = () => {
  const { bookstoreTypes, recurringBook, isNative, showBestSellers } = useTheme();
  const profile = useProfile();
  const { data: bookActivities } = useBookActivities();

  const type = bookstoreTypes.includes('magazine')
    ? 'magazine'
    : bookstoreTypes.includes('audiobook')
      ? bookstoreTypes[0] === 'audiobook'
        ? 'audiobook'
        : 'book'
      : 'book';

  const navigate = useNavigate();

  useEffect(() => {
    if (profile && isNative) {
      navigate('/acervo?isNative=true');
    }
  }, [profile, isNative]);

  return (
    <MainTemplate>
      {!isNative && <WhatsappFloatingBall />}
      <VStack marginTop="20px" spacing={8} marginBottom="150px" width={'100%'} align={'flex-start'}>
        <BannerAndCurrentBook />
        {bookstoreTypes.includes('book') && showBestSellers && (
          <HorizontalBooks
            hookApi={useMothlyBestSellers}
            name="Best Sellers do mês"
            hideIfEmpty
            description="Confira os livros que estão em alta e conquistando leitores."
            params={{}}
          />
        )}
        <BooksOfTheMonthSection recurringBook={recurringBook} />
        {bookstoreTypes.includes('book') && showBestSellers && (
          <HorizontalBooks
            name={'Explore os nossos Best Sellers'}
            params={{ type: type, random: 'true', is_best_seller: true }}
            hookApi={useBooks}
            showMore={() => navigate(bookstoreTypes.includes('book') ? '/acervo?bestsellers=true' : '/banca')}
            description={`
        Confira nossa seleção de ${
          bookstoreTypes.includes('book') ? 'Livros' : bookstoreTypes.includes('magazine') ? 'Revistas' : 'Audiolivros'
        } que estão em alta. Descubra novos títulos para sua coleção!
          `}
          />
        )}
        <HorizontalBooks
          name={`Explore o nossa ${bookstoreTypes.includes('book') ? 'acervo completo' : 'banca'}`}
          params={{ type: type, random: 'true', sortByName: 'true', limit: '50' }}
          hookApi={useBooks}
          showMore={() => navigate(bookstoreTypes.includes('book') ? '/acervo' : '/banca')}
          description={`
        Aqui você encontra nossa seleção de ${
          bookstoreTypes.includes('book') ? 'Livros' : bookstoreTypes.includes('magazine') ? 'Revistas' : 'Audiolivros'
        }. Explore e descubra novos títulos para sua coleção!
          `}
        />
        <BookActivitiesSection bookActivities={bookActivities} />
      </VStack>
    </MainTemplate>
  );
};
